import React, { useEffect, useState, type ReactNode } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import {
  reduceMotionAsPerUserPreference,
  useIsReducedMotion,
} from '@atlaskit/motion';
import { Box, xcss } from '@atlaskit/primitives';

const FOCUS_SETTLE_TIMEOUT = 3000;

const GradientPropertyContainer = styled.div({
  width: '100%',
  position: 'relative',
  '@property --gradient-angle': {
    syntax: `"<angle>"`,
    initialValue: '270deg',
    inherits: 'false',
  },

  '@keyframes rotationAnimation': {
    '0%': {
      '--gradient-angle': '0deg',
    },
    '100%': {
      '--gradient-angle': '360deg',
    },
  },
});

const outerContainerStyles = xcss({
  overflow: 'hidden',
  position: 'relative',
  borderRadius: '3px',
  background:
    'conic-gradient(from var(--gradient-angle, 45deg), #0065FF 10%, #0469FF 30%, #BF63F3 50%, #FFA900 70%, #0065FF 90%)',

  ...reduceMotionAsPerUserPreference,
});

const outerContainerStylesSettled = xcss({
  background: 'none',
  boxShadow: 'elevation.shadow.overlay',
});

const animationStyles = xcss({
  border: 'none',
  animation: 'rotationAnimation linear 2s infinite',
});

const glowStyles = xcss({
  position: 'absolute',
  filter: 'blur(20px)',
  opacity: '0.5',
  height: '100%',
  width: '100%',
});

const innerContainerStyles = xcss({
  borderRadius: '3px',
  backgroundColor: 'elevation.surface.overlay',
});

type PrismGlowContainerProps = {
  loading?: boolean;
  error?: boolean;
  children?: ReactNode;
};

export const PrismGlowContainer = ({
  loading = false,
  error = false,
  children,
}: PrismGlowContainerProps) => {
  const isReducedMotion = useIsReducedMotion();
  const [settle, setSettle] = useState(true);

  const shouldRotate = loading && !isReducedMotion;
  const containerStyles = [outerContainerStyles]
    .concat(shouldRotate ? [animationStyles] : [])
    .concat(settle || error ? [outerContainerStylesSettled] : []);

  useEffect(() => {
    if (loading) {
      setSettle(false);
    }
    if (!loading && !error) {
      setTimeout(() => setSettle(true), FOCUS_SETTLE_TIMEOUT);
    }
  }, [loading, error]);

  return (
    <GradientPropertyContainer>
      {shouldRotate && (
        <Box xcss={[outerContainerStyles, animationStyles, glowStyles]} />
      )}
      <Box padding={error ? undefined : 'space.025'} xcss={containerStyles}>
        <Box xcss={innerContainerStyles}>{children}</Box>
      </Box>
    </GradientPropertyContainer>
  );
};
